import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { clientMe, clientLogin, clientRegister, clientLogout, clientGoogleSession } from "../lib/api";

const ClientAuthContext = createContext(null);

const EMERGENT_AUTH_URL = "https://auth.emergentagent.com/";
const GOOGLE_TERMS_KEY = "fyllr_google_terms_accepted";

export function ClientAuthProvider({ children }) {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await clientMe();
      setClient(me);
      return me;
    } catch {
      setClient(null);
      return null;
    }
  }, []);

  // Handle Google redirect callback: Emergent returns #session_id=... in the hash.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const hash = window.location.hash || "";
      const match = hash.match(/session_id=([^&]+)/);
      if (match) {
        try {
          const termsAccepted = window.sessionStorage.getItem(GOOGLE_TERMS_KEY) === "true";
          await clientGoogleSession(decodeURIComponent(match[1]), { terms_accepted: termsAccepted });
          window.sessionStorage.removeItem(GOOGLE_TERMS_KEY);
        } catch {
          /* surfaced by UI on next action */
        }
        const clean = window.location.pathname + window.location.search;
        window.history.replaceState({}, document.title, clean);
      }
      await refresh();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const res = await clientLogin(email, password);
    setClient(res.client);
    return res.client;
  }, []);

  const register = useCallback(async (payload) => {
    const res = await clientRegister(payload);
    setClient(res.client);
    return res.client;
  }, []);

  const logout = useCallback(async () => {
    try { await clientLogout(); } finally { setClient(null); }
  }, []);

  const startGoogle = useCallback(({ termsAccepted = false } = {}) => {
    if (termsAccepted) window.sessionStorage.setItem(GOOGLE_TERMS_KEY, "true");
    else window.sessionStorage.removeItem(GOOGLE_TERMS_KEY);
    const redirect = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    window.location.href = `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(redirect)}`;
  }, []);

  return (
    <ClientAuthContext.Provider value={{ client, loading, login, register, logout, startGoogle, refresh }}>
      {children}
    </ClientAuthContext.Provider>
  );
}

export function useClientAuth() {
  const ctx = useContext(ClientAuthContext);
  if (!ctx) throw new Error("useClientAuth must be used within ClientAuthProvider");
  return ctx;
}
